import { useLocation, Navigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, Mail, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const PendingApprovalPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, betaAccess, loading, accountType, setupCompleted } = useUser();
  const email = location.state?.email;

  // CRITICAL: If signup verification flag is set, redirect immediately to role-selection
  // This user should NOT be on pending-approval - they haven't completed setup yet
  const isSignupVerification = sessionStorage.getItem('isSignupVerification') === 'true';
  if (isSignupVerification) {
    console.log('PendingApprovalPage: Signup verification flag detected, redirecting to role-selection');
    return <Navigate to="/role-selection" replace />;
  }

  // IMMEDIATE REDIRECTS using Navigate component - proper React pattern
  // This prevents flash of "pending approval" content for users still in setup
  if (!loading) {
    if (!accountType) {
      console.log('PendingApprovalPage: No account type, redirecting to role selection');
      return <Navigate to="/role-selection" replace />;
    }
    
    if (!setupCompleted) {
      console.log('PendingApprovalPage: Setup not completed, redirecting to onboarding');
      return <Navigate to={`/onboarding?type=${accountType}`} replace />;
    }
    
    if (betaAccess) {
      console.log('PendingApprovalPage: Has beta access, redirecting to dashboard');
      return <Navigate to="/" replace />;
    }
    
    if (!email && !currentUser) {
      console.log('PendingApprovalPage: No email or user, redirecting to beta signup');
      return <Navigate to="/beta-signup" replace />;
    }
  }

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth', { replace: true });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to sign out. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const displayEmail = email || currentUser?.email;

  // Only show loading during initial load - redirects above handle incomplete setup
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground">Checking account status...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Account Setup Complete!</CardTitle>
            <CardDescription className="text-base">
              Your profile is ready for review
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>{displayEmail}</span>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Thank you for completing your account setup! Our team is now reviewing your application.
                </p>
                <p className="text-sm font-medium">
                  You'll receive an email notification when your beta access is approved (usually within 24-48 hours).
                </p>
              </div>

              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-4">
                  While you wait, feel free to follow us on social media for updates about the platform.
                </p>
              </div>
            </div>

            {currentUser && (
              <Button
                onClick={handleSignOut}
                variant="outline"
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PendingApprovalPage;
